import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Video, Globe, FilmStrip, FileText, Camera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { fetchServices } from "@/lib/api";

const SERVICE_ICONS = {
  videotaped_deposition: Video,
  remote_videoconference: Globe,
  trial_presentation: FilmStrip,
  video_transcript_sync: FileText,
  exhibit_capture: Camera,
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1764410481612-7544525b2991?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwbHV4dXJ5fGVufDB8fHx8MTc4MjQzNzA3N3ww&ixlib=rb-4.1.0&q=85";
const VIDEO_IMG =
  "https://images.pexels.com/photos/29043652/pexels-photo-29043652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DOC_IMG =
  "https://images.pexels.com/photos/48195/document-agreement-documents-sign-48195.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1778731525581-9cff0deca568?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsYXclMjBmaXJtJTIwb2ZmaWNlJTIwbHV4dXJ5fGVufDB8fHx8MTc4MjQzNzA3N3ww&ixlib=rb-4.1.0&q=85";

const Landing = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices()
      .then(setServices)
      .catch(() => setServices([]));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Nav />

      {/* ---------- HERO ---------- */}
      <section className="relative" data-testid="hero-section">
        <div className="px-6 md:px-12 lg:px-24 pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 reveal">
            <div className="eyebrow mb-6">California Notary · Certified Legal Videographers</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-ink-900">
              Depositions,
              <br />
              recorded with the
              <br />
              gravity they deserve.
            </h1>
            <p className="mt-8 max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed">
              Del Cid Productions is a boutique legal-video studio headquartered in Hawthorne, California, serving
              litigators statewide and beyond. Two-camera HD, court-reporter-grade audio, and a videographer who is
              also a commissioned California Notary Public — ready to swear in your deponent on the record.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-5">
              <Link to="/book" data-testid="hero-book-btn">
                <Button className="rounded-none bg-ink-900 hover:bg-ink-800 text-white px-10 py-7 font-mono text-xs tracking-[0.22em] uppercase">
                  Reserve a videographer
                </Button>
              </Link>
              <a
                href="#services"
                data-testid="hero-services-link"
                className="font-mono text-xs tracking-[0.22em] uppercase border-b border-ink-900 pb-1 hover:text-champagne hover:border-champagne transition-colors"
              >
                See services →
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 reveal" style={{ animationDelay: "120ms" }}>
            <div className="relative">
              <img
                src={HERO_IMG}
                alt="Luxury law office"
                className="w-full h-[460px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-border p-6 max-w-[260px]">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-champagne">
                  Avg. response
                </div>
                <div className="font-display text-3xl text-ink-900 mt-1">11 min</div>
                <div className="text-xs text-muted-foreground mt-1">from inquiry to confirmed videographer</div>
              </div>
            </div>
          </div>
        </div>

        {/* trust ticker */}
        <div className="border-t border-b border-border bg-paper-warm">
          <div className="px-6 md:px-12 lg:px-24 py-6 flex flex-wrap items-center justify-between gap-6 text-xs font-mono uppercase tracking-[0.22em] text-muted-foreground">
            <span>California Notary Public</span>
            <span className="hidden md:inline">·</span>
            <span>FRCP 30(b)(3) Compliant</span>
            <span className="hidden md:inline">·</span>
            <span>HIPAA &amp; SOC 2 Aware</span>
            <span className="hidden md:inline">·</span>
            <span>4K + Backup Audio</span>
            <span className="hidden md:inline">·</span>
            <span>Same-day Rough Cut</span>
          </div>
        </div>
      </section>

      {/* ---------- SERVICES (Bento) ---------- */}
      <section id="services" data-testid="services-section" className="px-6 md:px-12 lg:px-24 py-24 lg:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-4">
            <div className="eyebrow">01 — Services</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl text-ink-900 leading-tight">
              Everything between the swearing in
              <br />
              <span className="italic text-champagne">and the certified record.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Featured large card */}
          {services[0] && (
            <ServiceCard service={services[0]} className="md:col-span-7 md:row-span-2 min-h-[520px]" image={VIDEO_IMG} large />
          )}
          {services[1] && <ServiceCard service={services[1]} className="md:col-span-5" />}
          {services[2] && <ServiceCard service={services[2]} className="md:col-span-5" />}
          {services[3] && <ServiceCard service={services[3]} className="md:col-span-6 min-h-[280px]" image={DOC_IMG} />}
          {services[4] && <ServiceCard service={services[4]} className="md:col-span-6 min-h-[280px]" />}
        </div>
      </section>

      {/* ---------- PROCESS ---------- */}
      <section id="process" data-testid="process-section" className="bg-ink-900 text-white relative overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow text-champagne">02 — Process</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight">
              Four steps.
              <br />
              No surprises on
              <br />
              the morning of.
            </h2>
            <img src={ABOUT_IMG} alt="Process" className="mt-10 w-full h-64 object-cover" />
          </div>

          <div className="lg:col-span-7 space-y-px bg-white/10">
            {[
              { n: "01", t: "Submit the booking", d: "Tell us the case caption, deponent, location, and time window. Twenty seconds." },
              { n: "02", t: "Coordinator confirms", d: "Within one business hour we confirm your videographer, equipment list, and parking notes." },
              { n: "03", t: "On the record", d: "We arrive 90 minutes early. Two cameras, redundant audio, time-coded and certified." },
              { n: "04", t: "Delivery", d: "Same-day rough cut, MPEG-1 + MDB sync within three business days. Chain-of-custody documented." },
            ].map((s) => (
              <div key={s.n} className="bg-ink-900 px-8 py-8 grid grid-cols-12 gap-6 items-start hover:bg-ink-800 transition-colors">
                <div className="col-span-2 font-mono text-champagne text-sm">{s.n}</div>
                <div className="col-span-10">
                  <div className="font-display text-2xl mb-2">{s.t}</div>
                  <p className="text-sm text-white/60 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" data-testid="faq-section" className="px-6 md:px-12 lg:px-24 py-24 lg:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="eyebrow">03 — FAQ</div>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-tight text-ink-900">
              The questions
              <br />
              every paralegal asks.
            </h2>
            <p className="mt-6 text-muted-foreground text-sm leading-relaxed">
              Don&apos;t see yours? Email{" "}
              <a href="mailto:booking@delcidproductions.com" className="text-ink-900 underline underline-offset-4 decoration-champagne">
                booking@delcidproductions.com
              </a>{" "}
              or call <a href="tel:+14242251267" className="text-ink-900 underline underline-offset-4 decoration-champagne">(424) 225-1267</a> —
              a coordinator replies within the hour.
            </p>
          </div>

          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="space-y-0 border-t border-border" data-testid="faq-accordion">
              {[
                {
                  q: "How far in advance must I book?",
                  a: "Most depositions can be confirmed within 48 hours. Same-day and weekend bookings are accepted subject to videographer availability — call us, we usually find a way.",
                },
                {
                  q: "What format do you deliver?",
                  a: "MPEG-1 by default with synced MDB or PTX files. We also deliver MP4, ProRes, and TrialDirector / OnCue-ready packages on request.",
                },
                {
                  q: "Do you provide remote / Zoom depositions?",
                  a: "Yes. Our Remote Videoconference service includes a dedicated technician monitoring the record, exhibit-share, and encrypted Zoom Pro rooms with waiting-room protocol.",
                },
                {
                  q: "What about cancellations?",
                  a: "Cancellations more than 48 hours out are free of charge. Inside 48 hours a session fee applies; reschedules within the same week are free. We&apos;ll always work with you on emergencies.",
                },
                {
                  q: "Are your videographers court-certified?",
                  a: "Every videographer is a Certified Legal Video Specialist (NCRA CLVS) — and a commissioned California Notary Public — carrying $2M of E&O coverage. We can swear in your deponent on the record and provide certifications on request.",
                },
              ].map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-border py-2"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="font-display text-xl lg:text-2xl text-ink-900 hover:no-underline text-left py-6">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="bg-paper-warm border border-border px-8 py-16 lg:p-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Ready when you are</div>
            <h3 className="font-display text-3xl lg:text-5xl text-ink-900 mt-3 leading-tight">
              Reserve your videographer in
              <br />
              under <span className="italic">two minutes</span>.
            </h3>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/book" data-testid="cta-book-btn">
              <Button className="rounded-none bg-ink-900 hover:bg-ink-800 text-white px-12 py-8 font-mono text-xs tracking-[0.22em] uppercase">
                Book a deposition →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ServiceCard = ({ service, className = "", image, large = false, minimal = false }) => {
  const Icon = SERVICE_ICONS[service.slug] || Video;

  if (large) {
    return (
      <Link
        to={`/book/${service.slug}`}
        data-testid={`service-card-${service.slug}`}
        className={`group relative overflow-hidden bg-ink-900 text-white p-10 lg:p-12 transition-all duration-500 hover:-translate-y-1 ${className}`}
      >
        <img
          src={image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity"
        />
        <div className="relative z-10 h-full flex flex-col justify-between">
          <div>
            <Icon size={36} weight="duotone" className="text-champagne" />
            <div className="eyebrow text-champagne mt-8">Featured service</div>
            <h3 className="font-display text-3xl lg:text-5xl mt-4 leading-tight max-w-xs">{service.title}</h3>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">{service.description}</p>
          </div>
          <div className="flex items-end justify-between mt-10">
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-champagne">Lead time</div>
              <div className="font-display text-2xl mt-1">{service.eta}</div>
            </div>
            <ArrowUpRight size={28} className="text-champagne group-hover:rotate-12 transition-transform" />
          </div>
        </div>
      </Link>
    );
  }

  if (minimal) {
    return (
      <Link
        to={`/book/${service.slug}`}
        data-testid={`service-card-${service.slug}`}
        className={`group bg-ink-900 text-white p-8 flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 ${className}`}
      >
        <Icon size={32} weight="duotone" className="text-champagne" />
        <div>
          <h3 className="font-display text-2xl leading-tight">{service.title}</h3>
          <div className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-champagne">
            {service.eta}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/book/${service.slug}`}
      data-testid={`service-card-${service.slug}`}
      className={`group relative bg-white border border-border p-8 lg:p-10 transition-all duration-500 hover:-translate-y-1 hover:border-ink-900 ${className}`}
    >
      {image && (
        <img
          src={image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover opacity-10 group-hover:opacity-20 transition-opacity"
        />
      )}
      <div className="relative z-10 h-full flex flex-col justify-between">
        <div>
          <Icon size={28} weight="duotone" className="text-ink-900" />
          <h3 className="font-display text-2xl mt-6 leading-tight text-ink-900">{service.title}</h3>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{service.summary}</p>
        </div>
        <div className="flex items-end justify-between mt-8 pt-6 border-t border-border">
          <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">Lead time</div>
            <div className="font-display text-xl text-ink-900 mt-1">{service.eta}</div>
          </div>
          <ArrowUpRight size={20} className="text-ink-900 group-hover:rotate-12 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

export default Landing;
